

import { useState } from "react";
import Badge from "./Badge";

interface Column<T> {
  key: string;
  header: string;
  render?: (item: T) => React.ReactNode;
  width?: string;
  align?: "left" | "center" | "right";
}

interface DataTableProps<T> {
  data: T[];
  columns: Column<T>[];
  keyExtractor: (item: T) => string;
  onRowClick?: (item: T) => void;
  actions?: (item: T) => React.ReactNode;
  emptyMessage?: string;
  searchable?: boolean;
  searchKeys?: (keyof T)[];
  mobileCardRender?: (item: T) => React.ReactNode;
}

export default function DataTable<T>({
  data,
  columns,
  keyExtractor,
  onRowClick,
  actions,
  emptyMessage = "No items found.",
  searchable = true,
  searchKeys = [],
  mobileCardRender,
}: DataTableProps<T>) {
  const [searchQuery, setSearchQuery] = useState("");

  const filteredData = searchable && searchQuery
    ? data.filter((item) =>
        searchKeys.some((key) => {
          const value = item[key];
          return (
            value &&
            String(value).toLowerCase().includes(searchQuery.toLowerCase())
          );
        })
      )
    : data;

  return (
    <div className="bg-white rounded-2xl border border-stone-100 shadow-sm overflow-hidden">
      {/* Search Bar */}
      {searchable && (
        <div className="p-3 border-b border-stone-100">
          <div className="flex items-center gap-2 bg-stone-50 rounded-xl px-3 py-2 max-w-sm">
            <svg className="w-4 h-4 text-stone-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search..."
              className="bg-transparent text-sm outline-none flex-1 placeholder-stone-400 text-stone-700"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="p-0.5 rounded-full hover:bg-stone-200 text-stone-400"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        </div>
      )}

      {/* Desktop Table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-stone-100 bg-stone-50">
              {columns.map((col) => (
                <th
                  key={col.key}
                  className={`px-4 py-3.5 text-xs font-semibold text-stone-500 uppercase tracking-wide ${
                    col.width || ""
                  } ${col.align === "center" ? "text-center" : col.align === "right" ? "text-right" : "text-left"}`}
                >
                  {col.header}
                </th>
              ))}
              {actions && (
                <th className="px-4 py-3.5 text-xs font-semibold text-stone-500 uppercase tracking-wide text-left">
                  Actions
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {filteredData.map((item) => (
              <tr
                key={keyExtractor(item)}
                onClick={() => onRowClick?.(item)}
                className={`border-b border-stone-50 hover:bg-amber-50/30 transition-colors ${
                  onRowClick ? "cursor-pointer" : ""
                }`}
              >
                {columns.map((col) => (
                  <td
                    key={col.key}
                    className={`px-4 py-4 ${
                      col.align === "center"
                        ? "text-center"
                        : col.align === "right"
                        ? "text-right"
                        : "text-left"
                    }`}
                  >
                    {col.render ? col.render(item) : String((item as any)[col.key] || "-")}
                  </td>
                ))}
                {actions && (
                  <td className="px-4 py-4" onClick={(e) => e.stopPropagation()}>
                    {actions(item)}
                  </td>
                )}
              </tr>
            ))}
            {filteredData.length === 0 && (
              <tr>
                <td
                  colSpan={columns.length + (actions ? 1 : 0)}
                  className="text-center py-10 text-stone-400"
                >
                  {emptyMessage}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile Cards */}
      <div className="md:hidden divide-y divide-stone-50">
        {filteredData.map((item) =>
          mobileCardRender ? (
            <div key={keyExtractor(item)}>{mobileCardRender(item)}</div>
          ) : (
            <div
              key={keyExtractor(item)}
              onClick={() => onRowClick?.(item)}
              className={`p-4 hover:bg-stone-50 transition-colors ${
                onRowClick ? "cursor-pointer" : ""
              }`}
            >
              <div className="space-y-2">
                {columns.slice(0, 3).map((col) => (
                  <div key={col.key} className="flex justify-between items-center">
                    <span className="text-xs text-stone-400">{col.header}</span>
                    <span className="text-sm text-stone-700">
                      {col.render ? col.render(item) : String((item as any)[col.key] || "-")}
                    </span>
                  </div>
                ))}
                {actions && (
                  <div className="pt-2 mt-2 border-t border-stone-100" onClick={(e) => e.stopPropagation()}>
                    {actions(item)}
                  </div>
                )}
              </div>
            </div>
          )
        )}
        {filteredData.length === 0 && (
          <div className="text-center py-10 text-stone-400">{emptyMessage}</div>
        )}
      </div>
    </div>
  );
}
